import { CommonModule, NgIf } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnInit,
  ViewChild
} from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { environment } from '../../../../../environments/environment'
import {
  catchError,
  combineLatest,
  filter,
  interval,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { AuthState, selectUser, User } from '../../../../store/auth/auth.state'
import { select, Store } from '@ngrx/store'
import { AuthService } from '../../../../services/auth.service'
import { ToastrService } from 'ngx-toastr'
import { loadEmployeeById } from '../../../../store/user/user.actions'
import {
  createClockInClockOut,
  createClockInClockOutFailure,
  createClockInClockOutSuccess,
  getLatestClockIn,
  loadEmployeeAttendance,
  updateClockIn,
  updateClockInFailure,
  updateClockInSuccess
} from '../../../../store/clockIn-clockOut/clockIn-clockOut.actions'
import { ClockInClockOut } from '../../../../store/clockIn-clockOut/clockIn-clockOut.state'
import {
  selectExpectedWorkHoursThisMonth,
  selectExpectedWorkHoursThisWeek,
  selectExpectedWorkHoursToday,
  selectIsPunchedIn,
  selectTodaysClockinoutRecords,
  selectTotalBreakHoursToday,
  selectTotalProductiveWorkHoursToday,
  selectTotalWorkHoursThisMonth,
  selectTotalWorkHoursThisWeek,
  selectTotalWorkHoursToday
} from '../../../../store/clockIn-clockOut/clockIn-clockOut.selectors'
import { SharedModule } from '../../../../shared/shared.module'
import {
  selectLatestPunchIn,
  selectLoading
} from '../../../../store/clockIn-clockOut/clockIn-clockOut.reducer'
import { Actions, ofType } from '@ngrx/effects'
import { UserService } from '../../../../services/user.service'
import { NotificationService } from '../../../../services/notification.service'
import { eps } from '../../../../services/url.service'
import { WorkStatus } from '../../attendance/calander/calander.component'
import { Notification } from '../../../../store/notification/notification.state'
import { Holiday } from '../../leaves/holidays/holidays.component'
import { HasPermissionDirective } from '../../../../directives/has-permission.directive'
import { selectUserPermissions } from '../../../../store/auth/auth.selectors'
import {
  ClockInClockOutService,
  ClockInOutCountResponse
} from '../../../../services/clockIn-clockOut.service'
import {
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  NgApexchartsModule
} from 'ng-apexcharts'
import { AttendancePolicyService } from '../../../../services/attendance-policy.service'

@Component({
  selector: 'app-owner-dashboard',
  imports: [
    ReactiveFormsModule,
    NgIf,
    CommonModule,
    RouterModule,
    FormsModule,
    HasPermissionDirective,
    SharedModule,
    NgApexchartsModule
  ],
  templateUrl: './owner-dashboard.component.html',
  styleUrl: './owner-dashboard.component.scss'
})
export class OwnerDashboardComponent {
  // Common Variables
  isPunchedIn = false
  public employeeId: string | undefined // Employee - employeeId = ''
  user: AuthState['user'] | null = null //Admin - user: any
  public apiEndpoint = environment.apiBaseUrl
  eps = eps

  // Employee Variables
  private store = inject(Store)
  private router = inject(Router)
  public defaultImg = '../../../../assets/img/user.jpg'
  public imagePath: any
  public imageURL: string | null = ''
  public attendanceChartOptions!: AttendanceChartOptions
  // employeeId = ''
  employee$: Observable<User | undefined> = of(undefined) // Initialize with a default value
  employeeImages: { [key: string]: string } = {} // Ensure this is defined correctly
  // user: AuthState['user'] | null = null
  user$: Observable<User | null> = this.store.select(selectUser)
  timeLabels: string[] = []
  public progressData: { start: string; width: string; color: string }[] = []
  isPunchedIn$: Observable<boolean> = this.store.pipe(select(selectIsPunchedIn))
  punchInId = ''
  latestPunchIn$: Observable<ClockInClockOut | null> = this.store.pipe(select(selectLatestPunchIn))
  latestPunchIn!: ClockInClockOut | null
  totalWorkHoursThisMonth = ''
  totalWorkHoursThisWeek = ''
  totalWorkHoursToday = ''
  todaysClockinoutRecords!: ClockInClockOut[]
  totalProductiveWorkHoursToday = ''
  totalBreakHoursToday = ''
  totalWorkHoursThisWeek$ = this.store.select(selectTotalWorkHoursThisWeek)
  totalWorkHoursThisMonth$ = this.store.select(selectTotalWorkHoursThisMonth)
  totalWorkHoursToday$ = this.store.select(selectTotalWorkHoursToday)
  todaysClockinoutRecords$ = this.store.select(selectTodaysClockinoutRecords)
  totalProductiveWorkHoursToday$ = this.store.select(selectTotalProductiveWorkHoursToday)
  totalBreakHoursToday$ = this.store.select(selectTotalBreakHoursToday)
  expectedWorkHoursToday$ = this.store.select(selectExpectedWorkHoursToday)
  expectedWorkHoursThisWeek$ = this.store.select(selectExpectedWorkHoursThisWeek)
  expectedWorkHoursThisMonth$ = this.store.select(selectExpectedWorkHoursThisMonth)
  currentDate = new Date()
  private destroyed$ = new Subject<void>()
  teamMembers!: User[]
  teamMembers$!: Observable<User[]>
  birthdayEmployees!: User[]
  birthdayEmployees$!: Observable<User[]>
  notifications!: Notification[]
  isNotificationsLoading = false
  workstatusEnum = WorkStatus
  isLoading$ = this.store.pipe(select(selectLoading))
  nextHoliday: Holiday | null = null
  linkPath: string[] = []
  clockInOutCount$!: Observable<ClockInOutCountResponse>
  queryParams: any = {}
  isBusinessOwner = false
  chartOptions: any
  defaultCount: ClockInOutCountResponse = {
    attendanceDetails: {
      onTime: 0,
      lateAttendance: 0,
      earlyLeave: 0,
      workFromHome: 0,
      absent: 0,
      sickLeave: 0,
      workedDays: 0,
      performance: '0'
    },
    leaveDetails: {
      totalLeaves: 0,
      taken: 0,
      absent: 0,
      request: 0,
      lossOfPay: 0,
      upcomingLeaves: 0
    },
    forceRefresh: false
  }

  // Admin Variables
  punchInTime: string | null = null
  punchOutTime: string | null = null
  // isPunchedIn = false
  totalWorkedHours: number | undefined
  intervalId: ReturnType<typeof setInterval> | undefined
  id: string | number | undefined
  todayDate: string | null | undefined
  // public employeeId: string | undefined
  employee: string | null | undefined
  itemData: unknown
  userId: unknown
  public clockInTime: string | null | undefined
  isEmployee = false
  // user: any
  public token = ''
  totalWorkedMinutes: number | undefined
  gpsWorkedHours: number | undefined
  gpsWorkedMinutes: number | undefined
  employees$!: Observable<User[]>
  employees!: User[]
  loading = false
  employeeCount = 0
  allEmployees: any[] = []
  filteredEmployees: any[] = []
  departmentCounts: { [key: string]: number } = {}
  totalEmployees = 0
  // selectedFilter: 'month' | 'week' | 'lastWeek' | 'year' | 'today' = 'year'
  selectedFilter: 'month' | 'week' | 'lastWeek' | 'year' | 'today' = 'month'
  selectedFilterLabel = 'This Month'
  chartData: { x: string; y: number }[] = []
  increasePercentage: number | null = null
  trendText: string | null = null
  thisPeriodCount = 0
  lastPeriodCount = 0
  xLabels: string[] = []
  maxLimit = 25
  Math = Math
  todayIndex!: number
  points: any[] = []
  segments: any[] = []
  employeeStats = {
    fullTime: { count: 0, percentage: 0 },
    probation: { count: 0, percentage: 0 },
    contract: { count: 0, percentage: 0 }
  }
  total = 0
  present = 0
  late = 0
  leave = 0
  absent = 0
  presentCount = 0
  lateCount = 0
  leaveCount = 0
  absentCount = 0
  hasData = false
  tooltip = { show: false, x: 0, y: 0, label: '', value: 0, percent: '' }
  onTimeUsers: any[] = []
  lateUsers: any[] = []
  expandedUser: string | null = null
  todayBirthdays: BirthdayUser[] = []
  tomorrowBirthdays: BirthdayUser[] = []
  upcomingBirthdays: BirthdayUser[] = []
  todayAnniversaries: any[] = []
  tomorrowAnniversaries: any[] = []
  upcomingAnniversaries: any[] = []
  selectedFilterOverview: 'month' | 'week' | 'lastWeek' | 'year' | 'today' = 'month'
  selectedFilterOverviewLabel = 'This Month'
  selectedFilterClockInOut: 'month' | 'week' | 'lastWeek' | 'year' | 'today' = 'month'
  selectedFilterClockInOutLabel = 'This Month'
  // private userService = inject(UserService)
  private attendancePolicyService = inject(AttendancePolicyService)
  private clockInOutService = inject(ClockInClockOutService)
  @ViewChild('attendanceCanvas')
  attendanceCanvas!: ElementRef<HTMLCanvasElement>

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private toasterNotification: ToastrService,
    private actions$: Actions,
    private userService: UserService,
    private notificationService: NotificationService,
    // private holidayService: HolidayService
    private clockInClockOutService: ClockInClockOutService,
    private zone: NgZone
  ) {
    this.generateTimeLabels()
  }

  ngOnInit() {
    // Employee Dashboard
    this.store
      .select(selectUser)
      .pipe(tap(user => (this.user = user)))
      .subscribe(user => {
        if (user) {
          // this.store.dispatch(loadEmployeeById({ id: user.id }))
          // this.loadAttendances()
          // this.store.dispatch(getLatestClockIn({ id: user.id }))
          // const params = ''
          // const page = 1
          // const limit = 5
          // this.teamMembers$ = this.userService
          //   .getTeamMembersOfEmployee(this.user?.id ?? '', params, page, limit)
          //   .pipe(map(res => res.teamMembers))
          // if (user.businessId) {
          //   this.getTeamMemberBirthday(user.businessId)
          // }
          // this.getNotifications(user.id)
        }
        // Leave Policy For Dashbaord
        this.store
          .select(selectUserPermissions)
          .pipe(take(1))
          .subscribe(perms => {
            const hasLeavePolicyView = perms['leavePolicy']?.includes('view')
            if (hasLeavePolicyView) {
              this.linkPath = ['/', eps.leaves.base, eps.leaves.policies]
            } else {
              this.linkPath = ['/', eps.hrm.base, eps.hrm.employee, user?.id]
              this.queryParams = { tab: 'leave' }
            }
          })
        // Upcoming Holiday
        // this.clockInClockOutService.upcomingHoliday(user?.id ?? '').subscribe(res => {
        //   this.nextHoliday = res ? ({ title: res.title, date: res.date } as Holiday) : null
        // })
        //Attendance Count
        this.clockInOutCount$ = this.clockInClockOutService.getClockInOutCount().pipe(
          catchError(() => of(this.defaultCount)),
          startWith(this.defaultCount),
          shareReplay(1)
        )
        // Hide Business Owner Progress
        if (!user) return
        this.isBusinessOwner = user.employmentType === 'Business Owner'
        // Chart
        // this.initAttendanceCha/rt()
        // this.bindAttendanceChartData()

        // Admin Dashboard
        this.setTodayIndex('year')
        this.loadEmployees()
        this.loadChartData()
        this.loadStatusChartData()
        this.loadAttendanceOverview()
        this.loadAttendance()
        this.loadBirthdays()
        this.loadAnniversaries()
      })

    // Employee Dashboard

    // Recalculate progress every minute
    combineLatest([
      this.todaysClockinoutRecords$,
      this.latestPunchIn$,
      interval(60000).pipe(startWith(0))
    ])
      .pipe(
        map(([records, latestPunchIn]) => {
          const hasOngoing =
            latestPunchIn?.workStatus === this.workstatusEnum.CLOCKIN && !latestPunchIn.clockOut

          let combinedRecords = [...records]

          if (hasOngoing) {
            const existing = records.find(r => r.id === latestPunchIn.id)
            if (!existing) {
              combinedRecords = [...records, latestPunchIn] // 👈 include ongoing punch-in
            }
          }

          return this.calculateProgressBars(combinedRecords)
        })
      )
      .subscribe(progressData => {
        this.progressData = progressData
      })

    setInterval(() => {
      this.currentDate = new Date()
    }, 1000)

    this.actions$
      .pipe(
        ofType(
          createClockInClockOutSuccess,
          createClockInClockOutFailure,
          updateClockInSuccess,
          updateClockInFailure
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        if (this.user) {
          this.store.dispatch(loadEmployeeById({ id: this.user?.id }))
          this.loadAttendances()
          this.store.dispatch(getLatestClockIn({ id: this.user?.id }))
        }
      })
  }

  generateTimeLabels() {
    this.timeLabels = Array.from({ length: 25 }, (_, i) => `${i.toString().padStart(2, '0')}:00`)
  }

  punchIn(): void {
    this.store.dispatch(createClockInClockOut({ clockIn: new Date() }))
    if (this.user) {
      this.store.dispatch(
        loadEmployeeAttendance({ id: this.user.id, params: `userId=${this.user.id}` })
      )
      this.store.dispatch(getLatestClockIn({ id: this.user.id }))
    }
  }

  punchOut() {
    this.latestPunchIn$
      .pipe(
        filter(latestPunchIn => latestPunchIn !== null),
        take(1)
      )
      .subscribe(latestPunchIn => {
        this.store.dispatch(updateClockIn({ id: latestPunchIn.id }))
      })
  }

  liveToday$ = this.createLiveTimer(this.totalWorkHoursToday$)
  liveWeek$ = this.createLiveTimer(this.totalWorkHoursThisWeek$)
  liveMonth$ = this.createLiveTimer(this.totalWorkHoursThisMonth$)
  totalWithBreakToday$ = combineLatest([this.liveToday$, this.totalBreakHoursToday$]).pipe(
    map(([work, breakTime]) => {
      const workMinutes = this.parseTimeToMinutes(work)
      const breakMinutes = this.parseTimeToMinutes(breakTime)

      return this.formatMinutes(workMinutes + breakMinutes)
    })
  )
  private parseTimeToMinutes(time: string | null): number {
    const match = /(?:(\d+)h)?\s*(?:(\d+)m)?/.exec(time ?? '')
    const hours = match ? parseInt(match[1] || '0', 10) : 0
    const minutes = match ? parseInt(match[2] || '0', 10) : 0
    return hours * 60 + minutes
  }
  private formatMinutes(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours}h ${minutes}m`
  }

  calculateProgressBars(records: ClockInClockOut[] = []): {
    start: string
    width: string
    color: string
  }[] {
    const totalMinutes = 1440 // 24 * 60
    const progressData: { start: string; width: string; color: string }[] = []

    const getMinutes = (time: Date | string): number => {
      const date = new Date(time)
      return date.getHours() * 60 + date.getMinutes()
    }

    const sorted = [...records].sort(
      (a, b) => new Date(a.clockIn).getTime() - new Date(b.clockIn).getTime()
    )

    const first = sorted[0]?.clockIn
    const firstMinutes = getMinutes(first)
    if (firstMinutes > 0) {
      progressData.push({
        start: '0%',
        width: `${(firstMinutes / totalMinutes) * 100}%`,
        color: 'bg-transparent'
      })
    }

    for (const record of sorted) {
      const inMinutes = getMinutes(record.clockIn)
      const outMinutes = record.clockOut ? getMinutes(record.clockOut) : getMinutes(new Date()) // ⏱ use now if not clocked out
      const startPct = Math.max(0, (inMinutes / totalMinutes) * 100)
      const endPct = Math.min(100, (outMinutes / totalMinutes) * 100)
      const width = Math.max(0, endPct - startPct)
      progressData.push({
        start: `${startPct.toFixed(2)}%`,
        width: `${width.toFixed(2)}%`,
        color: this.getWorkStatusColor(record.workStatus || this.workstatusEnum.CLOCKIN)
      })
    }
    return progressData
  }

  getWorkStatusColor(status: string | null) {
    switch (status) {
      case this.workstatusEnum.BREAK:
        return 'bg-warning'
      case this.workstatusEnum.PRESENT:
        return 'bg-success'
      case this.workstatusEnum.CLOCKIN:
        return 'bg-success'
      case 'Uninformed':
        return 'bg-info'
      case 'Overtime':
        return 'bg-secondary'
      default:
        return 'bg-white'
    }
  }

  loadAttendances() {
    const params: any = {}
    params.userId = this.user?.id
    this.store.dispatch(
      loadEmployeeAttendance({
        id: this.user?.id,
        params: new URLSearchParams(params).toString()
      })
    )
  }

  getEmployeeImage(employee: User): string {
    if (employee?.picture && this.employeeImages[employee.picture]) {
      return this.employeeImages[employee.picture]
    } else if (employee.picture) {
      const imageUrl = this.apiEndpoint + '/uploads/' + employee.picture // Assuming this is the correct path
      this.authService.getImage(imageUrl).subscribe(
        imageBlob => {
          const objectURL = URL.createObjectURL(imageBlob)
          this.employeeImages[employee.picture ?? ''] = objectURL // Store the object URL
        },
        error => {
          console.error('Error fetching image:', error)
        }
      )
    }
    return ''
  }

  private createLiveTimer(baseTime$: Observable<string>) {
    return combineLatest([
      this.latestPunchIn$,
      baseTime$,
      interval(60000).pipe(startWith(0)) // update every minute
    ]).pipe(
      map(([latestPunchIn, totalWorked]) => {
        // 🔹 Convert "0h 17m" → hours number
        const workedMatch = /(?:(\d+)h)?\s*(?:(\d+)m)?/.exec(totalWorked ?? '')
        const workedHours = workedMatch ? parseInt(workedMatch[1] || '0', 10) : 0
        const workedMinutes = workedMatch ? parseInt(workedMatch[2] || '0', 10) : 0
        let total = workedHours + workedMinutes / 60
        // 🔹 Add running time if currently clocked in
        if (
          latestPunchIn?.clockIn &&
          latestPunchIn.workStatus === this.workstatusEnum.CLOCKIN &&
          !latestPunchIn.clockOut
        ) {
          const clockInTime = new Date(latestPunchIn.clockIn).getTime()
          const now = Date.now()
          const diffInMs = now - clockInTime
          const diffInHours = diffInMs / (1000 * 60 * 60)

          total += diffInHours
        }
        // 🔹 Format back to "Xh Ym"
        const hours = Math.floor(total)
        const minutes = Math.floor((total - hours) * 60)
        return `${hours}h ${minutes}m`
      })
    )
  }

  private minuteTicker$ = interval(60000).pipe(
    startWith(0),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  readonly totalWorkHoursTodayCurrent$ = combineLatest([
    this.store.select(selectTotalWorkHoursToday),
    this.latestPunchIn$,
    this.minuteTicker$
  ]).pipe(
    map(([totalWorked, latestPunchIn]) => {
      const match = /(?:(\d+)h)?\s*(?:(\d+)m)?/.exec(totalWorked ?? '')
      const baseHours = match ? parseInt(match[1] || '0', 10) : 0
      const baseMinutes = match ? parseInt(match[2] || '0', 10) : 0
      let totalMs = (baseHours * 60 + baseMinutes) * 60 * 1000
      if (
        latestPunchIn?.clockIn &&
        latestPunchIn.workStatus === this.workstatusEnum.CLOCKIN &&
        !latestPunchIn.clockOut
      ) {
        const clockInTime = new Date(latestPunchIn.clockIn).getTime()
        totalMs += Date.now() - clockInTime
      }
      const hours = Math.floor(totalMs / (1000 * 60 * 60))
      const minutes = Math.floor((totalMs / (1000 * 60)) % 60)
      return `${hours}h ${minutes}m`
    }),
    shareReplay(1)
  )

  readonly totalWorkHoursIncludingCurrent$ = combineLatest([
    this.latestPunchIn$,
    this.totalWorkHoursToday$,
    interval(60000).pipe(startWith(0)) // update every minute
  ]).pipe(
    map(([latestPunchIn, totalWorked]) => {
      // Convert "0h 17m" => total hours as number
      const workedMatch = /(?:(\d+)h)?\s*(?:(\d+)m)?/.exec(totalWorked ?? '')
      const workedHours = workedMatch ? parseInt(workedMatch[1] || '0', 10) : 0
      const workedMinutes = workedMatch ? parseInt(workedMatch[2] || '0', 10) : 0
      let total = workedHours + workedMinutes / 60
      // Only add if ongoing punch (not finished yet)
      if (
        latestPunchIn?.clockIn &&
        latestPunchIn.workStatus === this.workstatusEnum.CLOCKIN &&
        !latestPunchIn.clockOut
      ) {
        const clockInTime = new Date(latestPunchIn.clockIn).getTime()
        const now = Date.now()
        const diffInMs = now - clockInTime
        const diffInHours = diffInMs / (1000 * 60 * 60)
        total += diffInHours
      }
      // Format back to "Xh Ym"
      const hours = Math.floor(total)
      const minutes = Math.round((total - hours) * 60)
      return `${hours}h ${minutes}m`
    })
  )

  getInitials(firstName = '', lastName = ''): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  getTeamMembersOfEmployee() {
    const params = ''
    const page = 1
    const limit = 5
    this.userService.getTeamMembersOfEmployee(this.employeeId, params, page, limit).subscribe({
      next: res => {
        this.teamMembers = res.teamMembers
      },
      error: err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch team members:', err)
      }
    })
  }

  getTeamMemberBirthday(businessId: string) {
    this.userService.getTeamMemberBirthday(businessId).subscribe({
      next: res => {
        this.birthdayEmployees = res
      },
      error: err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch team members:', err)
      }
    })
  }

  getNotifications(userId: string) {
    this.isNotificationsLoading = true
    const search = ''
    const page = 1
    const limit = 5
    this.notificationService.list(userId, search, page, limit).subscribe({
      next: res => {
        this.notifications = (res.notifications || []).map((n: Notification) => ({
          ...n,
          createdAt: new Date(n.createdAt as string | Date)
        }))
        this.isNotificationsLoading = false
      },
      error: err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch team members:', err)
        this.isNotificationsLoading = false
      }
    })
  }

  redirectToBilling() {
    this.router.navigate([eps.planAndBilling.planPath])
  }

  private initAttendanceChart() {
    this.attendanceChartOptions = {
      series: [],
      chart: {
        type: 'donut',
        height: 255,
        animations: {
          enabled: false
        }
      },
      labels: [],
      colors: [],
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          return opts.w.globals.series[opts.seriesIndex]
        }
      },
      legend: {
        show: false
      },
      stroke: {
        width: 2
      },
      tooltip: {
        enabled: true,
        fillSeriesColor: true,
        x: {
          show: false
        },
        y: {
          title: {
            formatter: (seriesName: string) => seriesName + ':'
          }
        }
      },
      plotOptions: {
        pie: {
          dataLabels: {
            offset: 0,
            minAngleToShowLabel: 0
          }
        }
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 220
            }
          }
        }
      ]
    }
  }

  private bindAttendanceChartData() {
    this.clockInOutCount$.pipe(takeUntil(this.destroyed$)).subscribe(count => {
      if (!count?.attendanceDetails) return
      const series = [
        Number(count.attendanceDetails.onTime || 0),
        Number(count.attendanceDetails.lateAttendance || 0),
        Number(count.attendanceDetails.workFromHome || 0),
        Number(count.attendanceDetails.absent || 0),
        Number(count.attendanceDetails.sickLeave || 0)
      ]
      const labels = ['On Time', 'Late Attendance', 'Work From Home', 'Absent', 'Sick Leave']
      const colors = ['#03C95A', '#0C4B5E', '#F26522', '#E70D0D', '#F59E0B']
      const filtered = series
        .map((value, index) => ({
          value,
          label: labels[index],
          color: colors[index]
        }))
        .filter(item => item.value > 0)
      if (filtered.length === 0) {
        this.attendanceChartOptions = {
          ...this.attendanceChartOptions
        }
        return
      }
      this.attendanceChartOptions = {
        ...this.attendanceChartOptions,
        series: filtered.map(i => i.value),
        labels: filtered.map(i => i.label),
        colors: filtered.map(i => i.color)
      }
    })
  }

  // Admin Dashboard

  loadEmployees() {
    this.loading = true
    this.userService.getFiveEmployees().subscribe({
      next: res => {
        this.employees = res
        this.loading = false
      },
      error: () => (this.loading = false)
    })
  }

  changeFilter(type: 'month' | 'week' | 'lastWeek' | 'year' | 'today', section?: string) {
    const label = this.getFilterLabel(type, section)
    console.log(section, type)
    if (section === 'employees by department') {
      this.selectedFilter = type
      this.selectedFilterLabel = label
      this.loadChartData()
    } else if (section === 'attendance overview') {
      this.selectedFilterOverview = type
      this.selectedFilterOverviewLabel = label
      this.loadAttendanceOverview()
    } else if (section === 'clock in out') {
      this.selectedFilterClockInOut = type
      this.selectedFilterClockInOutLabel = label
    }
  }

  loadChartData() {
    const params = `filter=${this.selectedFilter}`
    this.userService.getEmployeeChartData(params).subscribe(res => {
      this.chartData = res.chartData
      this.maxLimit = res.maxLimit
      this.thisPeriodCount = res.stats.thisPeriodCount
      this.lastPeriodCount = res.stats.lastPeriodCount
      this.increasePercentage = res.stats.changePercent
      this.trendText = res.stats.trend

      this.chartOptions = {
        series: [
          {
            name: 'Employees',
            data: this.chartData.map(d => d.y)
          }
        ],
        // chart: {
        //   type: 'line',
        //   height: 300,
        //   toolbar: { show: false }
        // },
        chart: {
          type: 'area',
          height: 320,
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        fill: {
          type: 'gradient',
          gradient: {
            // shadeIntensity: 1,
            // opacityFrom: 0.4,
            // opacityTo: 0.1
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        grid: {
          borderColor: '#f1f1f1'
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        xaxis: {
          categories: this.chartData.map(d => d.x)
        },
        yaxis: {
          min: 1,
          labels: {
            formatter: (value: number) => Math.round(value).toString()
          }
        },
        markers: {
          size: 5
        },
        // colors: ['#4caf50'],
        colors: ['#f26522'],
        dataLabels: {
          enabled: false
        }
      }
    })
  }

  setTodayIndex(chartType: 'week' | 'month' | 'year') {
    const today = new Date()
    switch (chartType) {
      case 'week':
        this.todayIndex = today.getDay() // 0 = Sun
        break
      case 'month':
        this.todayIndex = today.getDate() - 1 // 0-based index
        break
      case 'year':
        this.todayIndex = today.getMonth() // 0 = Jan
        break
    }
  }

  private getFilterLabel(
    type: 'month' | 'week' | 'lastWeek' | 'year' | 'today',
    section?: string
  ): string {
    switch (type) {
      case 'today':
        return 'Today'
      case 'week':
        return 'This Week'
      case 'lastWeek':
        return 'Last Week'
      case 'month':
        return 'This Month'
      case 'year':
        return 'This Year'
      default:
        return ''
    }
  }

  get thisLabel(): string {
    switch (this.selectedFilter) {
      case 'week':
        return 'this Week'
      case 'lastWeek':
        return 'last Week'
      case 'month':
        return 'this Month'
      case 'year':
        return 'this Year'
      default:
        return 'this Period'
    }
  }

  get lastLabel(): string {
    switch (this.selectedFilter) {
      case 'week':
        return 'last Week'
      case 'lastWeek':
        return 'prev Week'
      case 'month':
        return 'last Month'
      case 'year':
        return 'last Year'
      default:
        return 'last Period'
    }
  }

  getLineChartPoints(): string {
    const weekMap = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const today = new Date()
    const todayDay = today.getDate()
    const todayWeekDay = today.getDay()
    const todayMonth = today.getMonth()
    return this.chartData
      .map((point, i) => {
        if (this.selectedFilter === 'month') {
          const pointDay = Number(point.x)
          if (pointDay > todayDay && point.y === 0) return null
        }
        if (this.selectedFilter === 'week') {
          const pointDayIndex = weekMap.indexOf(point.x)
          if (pointDayIndex > todayWeekDay && point.y === 0) return null
        }
        if (this.selectedFilter === 'year') {
          if (i > todayMonth && point.y === 0) return null
        }
        return `${this.getPointX(i)},${this.getPointY(point.y)}`
      })
      .filter(p => p !== null)
      .join(' ')
  }

  buildChart() {
    const max = Math.max(...this.chartData.map(d => d.y))
    const stepX = 100 / (this.chartData.length - 1)
    this.points = this.chartData.map((d, i) => ({
      x: stepX * i,
      y: (d.y / max) * 100,
      value: d.y
    }))
    // Create connecting lines
    for (let i = 0; i < this.points.length - 1; i++) {
      const p1 = this.points[i]
      const p2 = this.points[i + 1]
      const dx = p2.x - p1.x
      const dy = p2.y - p1.y
      const length = Math.sqrt(dx * dx + dy * dy)
      const angle = Math.atan2(dy, dx) * (180 / Math.PI)
      this.segments.push({
        left: p1.x + '%',
        bottom: p1.y + '%',
        width: length + '%',
        transform: `rotate(${angle}deg)`
      })
    }
  }

  isFutureZero(point: any, i: number): boolean {
    const weekMap = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const today = new Date()
    if (this.selectedFilter === 'month') {
      return Number(point.x) > today.getDate() && point.y === 0
    }
    if (this.selectedFilter === 'week') {
      return weekMap.indexOf(point.x) > today.getDay() && point.y === 0
    }
    if (this.selectedFilter === 'year') {
      return i > today.getMonth() && point.y === 0
    }
    return false
  }

  getPointX(i: number): number {
    return i * (460 / this.chartData.length) + 50 + 460 / this.chartData.length / 2
  }

  getPointY(value: number): number {
    return 270 - (Math.min(value, this.maxLimit) / this.maxLimit) * 300
  }

  loadStatusChartData() {
    this.userService.getEmployeeStatusChartData().subscribe(res => {
      this.employeeStats = res.employeeTypeStats
      this.employeeCount = res.totalCount
    })
  }
  ngAfterViewInit() {
    this.drawGauge()
  }

  loadAttendanceOverview() {
    const params = `filter=${this.selectedFilterOverview}`
    this.attendancePolicyService.getSummary(params).subscribe(res => {
      this.total = res.total
      this.present = res.presentPercent
      this.late = res.latePercent
      this.leave = res.leavePercent
      this.absent = res.absentPercent
      this.presentCount = res.presentCount
      this.lateCount = res.lateCount
      this.leaveCount = res.leaveCount
      this.absentCount = res.absentCount
      this.hasData = this.total >= 0
      if (this.hasData) {
        setTimeout(() => this.drawGauge(), 0)
      }
    })
  }

  getDashArray(value: number) {
    const circumference = 440
    return `${(value / 100) * circumference} ${circumference}`
  }

  drawGauge() {
    const canvas = this.attendanceCanvas.nativeElement
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const cx = canvas.width / 2
    const cy = canvas.height - 20
    const radius = 100
    const thickness = 30
    const totalAngle = Math.PI // half-circle
    const gap = 0.02 // gap between segments
    // Attendance data
    const data = [
      { label: 'Present', value: this.presentCount, color: '#00c853' },
      { label: 'Late', value: this.lateCount, color: '#083d4f' },
      { label: 'Leaves', value: this.leaveCount, color: '#ffc107' },
      { label: 'Absent', value: this.absentCount, color: '#e53935' }
    ]
    const total = data.reduce((sum, item) => sum + item.value, 0)
    let startAngle = Math.PI // start from left side
    this.segments = []
    data.forEach(item => {
      if (item.value <= 0) return
      const segmentAngle = (item.value / total) * totalAngle
      const drawAngle = segmentAngle - gap
      if (drawAngle <= 0) {
        startAngle += segmentAngle
        return
      }
      const segStart = startAngle + gap / 2
      const segEnd = segStart + drawAngle
      // Draw segment
      ctx.beginPath()
      ctx.arc(cx, cy, radius, segStart, segEnd)
      ctx.lineWidth = thickness
      ctx.strokeStyle = item.color
      ctx.lineCap = 'round'
      ctx.stroke()
      // Store for hover
      this.segments.push({
        start: segStart,
        end: segEnd,
        label: item.label,
        value: item.value,
        percent: ((item.value / total) * 100).toFixed(0) // store percentage
      })
      startAngle += segmentAngle
    })
    // Draw total text
    ctx.textAlign = 'center'
    ctx.fillStyle = '#6c757d'
    ctx.font = '14px Arial'
    ctx.fillText('Total Attendance', cx, cy - 40)
    ctx.fillStyle = '#0b1b34'
    ctx.font = 'bold 28px Arial'
    ctx.fillText(String(total), cx, cy - 10)
    this.addHoverListener(canvas, cx, cy, radius, thickness)
  }

  addHoverListener(
    canvas: HTMLCanvasElement,
    cx: number,
    cy: number,
    r: number,
    thickness: number
  ) {
    canvas.onmousemove = e => {
      const rect = canvas.getBoundingClientRect()
      const canvasX = e.clientX - rect.left
      const canvasY = e.clientY - rect.top
      const dx = canvasX - cx
      const dy = canvasY - cy
      const distance = Math.sqrt(dx * dx + dy * dy)
      if (distance <= r + thickness / 2 && distance >= r - thickness / 2) {
        let angle = Math.atan2(dy, dx)
        if (angle < 0) angle += 2 * Math.PI
        for (const seg of this.segments) {
          if (angle >= seg.start && angle <= seg.end) {
            this.zone.run(() => {
              this.tooltip = {
                show: true,
                x: canvasX,
                y: canvasY - 30,
                label: seg.label,
                value: parseInt(seg.percent),
                percent: seg.percent + '%'
              }
            })
            return
          }
        }
      }

      this.zone.run(() => {
        this.tooltip.show = false
      })
    }
  }
  @HostListener('window:scroll')
  onWindowScroll() {
    this.tooltip.show = false
  }

  loadAttendance() {
    this.clockInOutService.getTodayClockIns().subscribe((res: any) => {
      this.onTimeUsers = res.onTimeUsers
      this.lateUsers = res.lateUsers
      // eslint-disable-next-line no-console
      console.log(this.lateUsers)
    })
  }

  toggleUserDetails(user: any) {
    this.expandedUser = this.expandedUser === user.id ? null : user.id
  }

  // punchIn(): void {
  //   //
  // }

  loadBirthdays() {
    this.userService.getCelebrations({ month: true, type: 'birthday' }).subscribe((res: any) => {
      this.todayBirthdays = res.today || []
      this.tomorrowBirthdays = res.tomorrow || []
      this.upcomingBirthdays = res.upcoming || []
    })
  }

  formatBirthday(dateStr: string): string {
    const date = new Date(dateStr)

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short'
    }) // 20 Feb
  }

  loadAnniversaries() {
    this.userService.getCelebrations({ month: true, type: 'anniversary' }).subscribe((res: any) => {
      if (res.type === 'monthly') {
        this.todayAnniversaries = res.today || []
        this.tomorrowAnniversaries = res.tomorrow || []
        this.upcomingAnniversaries = res.upcoming || []
      }
    })
  }

  formatAnniversary(date: string) {
    return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  }

  getWorkYears(joinDate: string): number {
    const join = new Date(joinDate)
    const now = new Date()
    return now.getFullYear() - join.getFullYear()
  }

  // punchOut(): void {
  //   //
  // }

  // Start a timer to update punch hours every minute (or second if needed)
  startPunchTimer(): void {
    this.intervalId = setInterval(() => {
      this.calculatePunchHours() // Recalculate every minute
    }, 60000) // 60000 ms = 1 minute
  }

  // Updated punch.component.ts
  calculatePunchHours(): void {
    //
  }

  getEmployee(): void {
    //
  }

  getLatestPunchIn(): void {
    //
  }

  getUser(): void {
    //
  }
}

export interface AttendanceChartOptions {
  series: ApexNonAxisChartSeries
  chart: ApexChart
  labels: string[]
  responsive: ApexResponsive[]
  legend: ApexLegend
  dataLabels: ApexDataLabels
  colors: string[]
  stroke: ApexStroke
  tooltip: ApexTooltip
  plotOptions?: ApexPlotOptions
}

export interface ChartOptions {
  series: ApexNonAxisChartSeries
  chart: ApexChart
  labels: string[]
  responsive: ApexResponsive[]
  legend: ApexLegend
}

interface BirthdayUser {
  id: string
  firstName: string
  lastName: string
  picture: string | null
  birthdate: string
  designation?: {
    designationName: string
    department?: { departmentName: string }
  } | null
}
